import { InjectionToken, Signal } from '@angular/core';
import { ai as AiGen, challenge as ChallengeGen } from '@valvur/shared-grpc';
import { CourseSettingsData } from '../../../models';

/**
 * Layout-specific façade for course-creator mutations (create draft, publish, generate media).
 *
 * **Host checklist** (each layout that embeds `CourseCreatorComponent`):
 * 1. Register a bridge: `providers: [{ provide: COURSE_CREATOR_BRIDGE, useClass: … }]`.
 * 2. Pass UI / flow inputs on `<app-course-creator>`: `steps`, `layoutVariant`, `courseCatalogSource`, `selectedCourse`, `initialStep`.
 * 3. Own list/catalog queries separately (e.g. school → `CourseService` + `schoolTeacherCoursesListsEnabled`; admin → `AdminContentService` + `valvurCourses`).
 *
 * The creator subtree only calls this token — it does not choose invalidation keys; that stays inside the bridge implementation (today delegating to `CourseService` mutations).
 *
 * Provide on the host component, for example:
 * `{ provide: COURSE_CREATOR_BRIDGE, useClass: SchoolCourseCreatorBridge }`
 */
export interface CourseCreatorBridge {
  /** POST /courses (multipart school flow). */
  createSchoolDraft(payload: CourseSettingsData): void;

  /** POST /courses/valvur (admin JSON catalog). */
  createAdminValvurDraft(payload: CourseSettingsData): void;

  publishCourse(courseId: string): Promise<ChallengeGen.Course>;

  generateVideo(dto: AiGen.GenerateVideoRequestDto): Promise<ChallengeGen.Task>;

  generateAudio(dto: AiGen.GenerateAudioRequestDto): Promise<ChallengeGen.Task>;

  readonly isPublishPending: Signal<boolean>;
}

export const COURSE_CREATOR_BRIDGE = new InjectionToken<CourseCreatorBridge>(
  'CourseCreatorBridge',
);
